import { PaginatedDataDTO } from '@bottega52/commons-pagination';
import MomentUtils from '@date-io/moment';
import { Button, Divider, TextField } from "@material-ui/core";
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { Autocomplete } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import * as React from "react";
import { connect, ConnectedProps } from 'react-redux';
import Select from 'react-select';
import * as CreditsActions from '../../redux/credits/credits.actions';
import * as ModalsActions from '../../redux/modals/modals.actions';
import * as SettingsActions from '../../redux/settings/settings.actions';
import { IState } from '../../redux/store';
import IClusterInDTO from '../../repository/jago/model/input/IClusterInDTO';
import { ICustomerFormDTO } from '../../repository/jago/model/input/ICustomerFormDTO';
import { IKamInDTO } from '../../repository/jago/model/input/IKamInDTO';
import IMarketplaceInDTO from '../../repository/jago/model/input/IMarketplaceInDTO';
import { IVarInDTO } from '../../repository/jago/model/input/IVarInDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS, Roles } from '../../services/PermissionService/PermissionConstants';
import Utils, { CountryInfo, ISEOCompanyInfo } from '../../utils/Utils';
import ReduxLanguage from "../ReduxLanguage";
import { ModalTypes } from '../Modals/ModalTypes';
import { CustomerCategoryType } from '../../codec/varInDTODecoder';
import Permission from '../Permission/Permission';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    padding: 20,
    height: '100%',
    flex: 1,
  },
  formTitle: {
    fontWeight: 'bold',
    marginBottom: 20,
  },
  errorText: {
    color: 'red'
  },
  inputTitle: {
    marginTop: 20,
    marginBottom: 8,
    fontSize: 18,
    fontWeight: 'bold',
  },
  disabledButton: {
    backgroundColor: 'red'
  },
  inputTitleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  italicText: {
    fontStyle: 'italic',
  },
  customerDataColumn: {
    flex: 1,
  }
});

export interface ICustomerFormState {
  customer: ICustomerFormDTO;
  isEditing: boolean;
  sfdcSyncCompleted: boolean;
  selectedCustomerType: CustomerCategoryType;
  ISEOCompanies: ISEOCompanyInfo[];
  countries: CountryInfo[];
  errors: Record<keyof ICustomerFormDTO, boolean>;
};

export interface ICustomerFormProps extends WithStyles<typeof styles>{
  onCreateNewCustomer: (customer: ICustomerFormDTO) => void;
  customer?: ICustomerFormDTO;
  forcedVar?: IVarInDTO;
  forcedCluster?: IClusterInDTO;
  forcedMarketplace?: IMarketplaceInDTO;
  isVar: boolean;
  isVarDealer?: boolean;
}

type IReduxProps = ConnectedProps<typeof connector>;

export type ComponentProps = ICustomerFormProps & IReduxProps;
class CustomerForm extends React.Component<ComponentProps, ICustomerFormState> {

  constructor(props: ComponentProps) {
    super(props);
    const defaultMarketplace = props.forcedMarketplace ? props.forcedMarketplace : (props.marketplaces as PaginatedDataDTO<IMarketplaceInDTO>).content.find(e=>e.defaultMarketplace) || null;
    this.state = {
      ISEOCompanies: Utils.getAvailableISEOCompanies(),
      countries: Utils.getAvailableCountries(),
      sfdcSyncCompleted: props.customer || props.isVar || props.isVarDealer ? true : false,
      selectedCustomerType: props.customer && !props.customer.vat
        ? CustomerCategoryType.SIMPLIFIED_CUSTOMER
        : CustomerCategoryType.CUSTOMER,
      isEditing: props.customer ? true : false,
      customer: props.customer ? props.customer : {
        name: '',
        marketplace:  defaultMarketplace,
        vat: '',
        fiscalCode: '',
        country: '',
        address: '',
        city: '',
        crmId: '',
        email: '',
        certifiedEmail: '',
        kamEmail: '',
        phone: '',
        cluster: props.forcedCluster?props.forcedCluster:null,
        company: '',
        postalCode: '',
        principalSubDivision: '',
        VAR: props.forcedVar?props.forcedVar:null,
      },
      errors: {
        name: false,
        marketplace: false,
        vat: false,
        fiscalCode: false,
        country: false,
        address: false,
        city: false,
        crmId: false,
        email: false,
        certifiedEmail: false,
        kamEmail: false,
        phone: false,
        cluster: false,
        company: false,
        postalCode: false,
        principalSubDivision: false,
        VAR: false,
      },
    };
  }

  onChangeValue(field: keyof ICustomerFormDTO, mandatory: boolean, value?: any) {
    const { customer, errors } = this.state;
    this.setState({
      errors: { ...errors, [field]: !value && mandatory ? true : false }
    });
    if (value) {
      (customer as any)[field] = value;
    }
    else {
      if (field === 'VAR') {
        (customer as any)[field] = null;
      }
    }
    this.setState({ customer });
  }

  onSubmitForm() {
    const { customer } = this.state;
    if (!this.canSubmitForm()) {
      return;
    }
    const { onCreateNewCustomer } = this.props;
    onCreateNewCustomer(customer);
  }

  canSubmitForm() {
    const { customer, selectedCustomerType } = this.state;
    const { isVar, isVarDealer } = this.props;
    const isSimplifiedCustomer = isVarDealer || selectedCustomerType === CustomerCategoryType.SIMPLIFIED_CUSTOMER;
    if (isSimplifiedCustomer) {
      return customer.name && customer.country && customer.company;
    }
    return customer.name && customer.vat && customer.country && 
    (isVar || (!_.isEmpty(customer.marketplace) && !_.isEmpty(customer.cluster) && customer.crmId && customer.company));
  }

  async verifyCustomerVat() {
    const { dispatch } = this.props;
    const { customer } = this.state;
    try { 
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const request = await dispatch<any>(CreditsActions.verifyCustomerVat(customer.vat));
      if (request && request.transactionId) {
        const synchedCustomersData = await dispatch<any>(CreditsActions.monitorVatVerificationUntilCompletion(request.transactionId));
        dispatch<any>(SettingsActions.setSpinnerVisible(false));
        if (synchedCustomersData) {
          dispatch<any>(ModalsActions.showModal(`SUCCESS_ALERT`, {
            modalType: ModalTypes.SUCCESS_ALERT,
            modalProps: {
              titleMessageKey: 'forms.synchronizationCompleted',
              }
            }));
          this.onChangeValue('crmId', true, synchedCustomersData.accountId || 'N/A');
          this.onChangeValue('country', true, synchedCustomersData.country || 'N/A');
          this.onChangeValue('address', false, synchedCustomersData.address || 'N/A');
          this.onChangeValue('name', true, synchedCustomersData.accountName || 'N/A');
          this.onChangeValue('city', false, synchedCustomersData.city || 'N/A');
          this.onChangeValue('phone', false, synchedCustomersData.phone || 'N/A');
          this.onChangeValue('email', false, synchedCustomersData.email || 'N/A');
          this.onChangeValue('fiscalCode', false, customer.vat);

          this.setState({ sfdcSyncCompleted: true });
        } else {
          dispatch<any>(ModalsActions.showModal(`ERROR_ALERT`, {
            modalType: ModalTypes.ERROR_ALERT,
            modalProps: {
              titleMessageKey: 'forms.verifyCustomerVatError',
            }
          }));
        }
      }
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_ALERT`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'forms.verifyCustomerVatError',
        }
      }));
      console.log(error);
    }
  }

  public render() {
    const { classes, marketplaces, clusters, vars, kams, customer: customerToEdit, isVar, isVarDealer } = this.props;
    const { customer, errors, ISEOCompanies, countries, sfdcSyncCompleted, selectedCustomerType, isEditing } = this.state;

    const canEdit = (customerToEdit && AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CUSTOMER_UPDATE)) || 
      (!customerToEdit && AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CUSTOMER_CREATE))
    const isSofiaAdmin = AbilityProvider.getAbilityHelper().hasRoles([Roles.ROLE_VASHO_SOFIA_ADMIN]);
    const isVatControlEnabled = !isVar &&  !sfdcSyncCompleted && !isSofiaAdmin;
    const isSimplifiedCustomer = isVarDealer || (selectedCustomerType === CustomerCategoryType.SIMPLIFIED_CUSTOMER);

    return (
      <MuiPickersUtilsProvider utils={MomentUtils} locale={moment.locale()}>
        <div className={classes.container}>
          {!isVarDealer && !isVar && !isEditing ? (
            <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.CUSTOMER_SIMPLIFIED_CUSTOMER_CREATE}>
              <Select
              options={_.map(CustomerCategoryType, (v) => ({ 
                value: v, 
                label: Utils.getTranslatedCustomerCategoryLabel(v) 
              }))}
                isClearable={false}
                getOptionLabel={o => o.label}
                isOptionSelected={(o) => selectedCustomerType === o.value}
                placeholder={<ReduxLanguage languageKey={'vars.varCategoryType'} />}
                onChange={(data) => this.setState({ selectedCustomerType: data?.value })}
                styles={{
                  menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
                  control: (styles) => ({ ...styles, minHeight: 55, marginTop: 20 }),
                }} 
              />
              <Divider style={{ marginTop: 20, marginBottom: 20 }} />
            </Permission>
          ) : null}
          {!isVar && !isSimplifiedCustomer ? (
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: 0, marginBottom: 20 }}>
              <h2 className={classes.formTitle}>{<ReduxLanguage languageKey={'forms.customerVatVerificationTitle'} />}</h2>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <TextField
                  id="outlined-basic"
                  label={<ReduxLanguage languageKey={'forms.customerVat'} />}
                  variant="outlined"
                  fullWidth
                  required
                  disabled={!canEdit}
                  error={errors.vat}
                  value={customer.vat}
                  style={{ marginTop: 0, marginRight: 20 }}
                  onChange={e => this.onChangeValue('vat', true, e.target.value)}
                />
                <Button variant="contained" style={{ marginTop: 0, width: 300, backgroundColor: '#5AC0B1', color: 'white', fontWeight: 'bold' }} onClick={() => this.verifyCustomerVat()}>
                  <ReduxLanguage languageKey={'forms.sfdcSync'} />
                </Button>
              </div>
            </div>
          ): null}
            <div>
              <h2 style={{ fontWeight: 'bold', opacity: !isVatControlEnabled ? 1 : 0.6 }}>{<ReduxLanguage languageKey={'forms.customerData'} />}</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div style={{ flex: 1, marginRight: 10, opacity: !isVatControlEnabled ? 1 : 0.6 }}>
              <TextField
                id="outlined-basic"
                label={<ReduxLanguage languageKey={'forms.customerName'} />}
                variant="outlined"
                fullWidth
                required
                disabled={!canEdit || isVatControlEnabled}
                error={errors.name}
                value={customer.name}
                style={{ marginTop: 20 }}
                onChange={e => this.onChangeValue('name', true, e.target.value)}
              />
              {isVar && !isSimplifiedCustomer ? (
                <TextField
                  id="outlined-basic"
                  label={<ReduxLanguage languageKey={'forms.customerVat'} />}
                  variant="outlined"
                  fullWidth
                  required
                  disabled={!canEdit || isVatControlEnabled}
                  error={errors.vat}
                  value={customer.vat}
                  style={{ marginTop: 20 }}
                  onChange={e => this.onChangeValue('vat', true, e.target.value)}
                />
              ): null}
              {!isSimplifiedCustomer ? (
                <>
                  <TextField
                    id="outlined-basic"
                    label={<ReduxLanguage languageKey={'forms.customerFiscalCode'} />}
                    variant="outlined"
                    fullWidth
                    disabled={!canEdit || isVatControlEnabled}
                    error={errors.fiscalCode}
                    value={customer.fiscalCode}
                    style={{ marginTop: 20 }}
                    onChange={e => this.onChangeValue('fiscalCode', false, e.target.value)}
                  />
                  <TextField
                    id="outlined-basic"
                    label={<ReduxLanguage languageKey={'forms.customerEmail'} />}
                    variant="outlined"
                    fullWidth
                    disabled={!canEdit || isVatControlEnabled}
                    error={errors.email}
                    value={customer.email}
                    style={{ marginTop: 20 }}
                    onChange={e => this.onChangeValue('email', false, _.trim(e.target.value))}
                  />

                  <TextField
                    id="outlined-basic"
                    label={<ReduxLanguage languageKey={'forms.customerCertifiedEmail'} />}
                    variant="outlined"
                    fullWidth
                    disabled={!canEdit || isVatControlEnabled}
                    error={errors.certifiedEmail}
                    value={customer.certifiedEmail}
                    style={{ marginTop: 20 }}
                    onChange={e => this.onChangeValue('certifiedEmail', false, _.trim(e.target.value))}
                  />

                  <TextField
                    id="outlined-basic"
                    label={<ReduxLanguage languageKey={'forms.customerPhone'} />}
                    variant="outlined"
                    fullWidth
                    disabled={!canEdit || isVatControlEnabled}
                    error={errors.phone}
                    value={customer.phone}
                    style={{ marginTop: 20 }}
                    onChange={e => this.onChangeValue('phone', false, e.target.value)}
                  />
                </>
              ): null}
              <TextField
                id="outlined-basic"
                label={<ReduxLanguage languageKey={'forms.customerPhone'} />}
                variant="outlined"
                fullWidth
                disabled={!canEdit || isVatControlEnabled}
                error={errors.phone}
                value={customer.phone}
                style={{ marginTop: 20 }}
                onChange={e => this.onChangeValue('phone', false, e.target.value)}
              />
            </div>
            <div style={{ flex: 1, marginLeft: 10, opacity: sfdcSyncCompleted ? 1 : 0.6 }}>
              <Select
                options={countries}
                isClearable
                isDisabled={!canEdit || isVatControlEnabled}
                value={_.find(countries, { value: customer.country })}
                formatOptionLabel={(o) => <>{o.value}</>}
                isOptionSelected={(o, selectedValues) => _.findIndex(selectedValues, (sv) => sv === o) >= 0}
                placeholder={<><ReduxLanguage languageKey={'forms.customerCountry'} /> *</>}
                onChange={(data) => this.onChangeValue('country', true, data?.value)}
                required
                styles={{
                  menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
                  control: (styles) => ({ ...styles, minHeight: 55, marginTop: 20 }),
                }} 
              />
              {!isSimplifiedCustomer ? (
                <>
                  <TextField
                    id="outlined-basic"
                    label={<ReduxLanguage languageKey={'forms.customerAddress'} />}
                    variant="outlined"
                    fullWidth
                    disabled={!canEdit || isVatControlEnabled}
                    error={errors.address}
                    value={customer.address}
                    style={{ marginTop: 20 }}
                    onChange={e => this.onChangeValue('address', false, e.target.value)}
                  />
                  <TextField
                    id="outlined-basic"
                    label={<ReduxLanguage languageKey={'forms.customerCity'} />}
                    variant="outlined"
                    fullWidth
                    disabled={!canEdit || isVatControlEnabled}
                    error={errors.city}
                    value={customer.city}
                    style={{ marginTop: 20 }}
                    onChange={e => this.onChangeValue('city', false, e.target.value)}
                  />
                  <TextField
                    id="outlined-basic"
                    label={<ReduxLanguage languageKey={'forms.customerPostalCode'} />}
                    variant="outlined"
                    fullWidth
                    disabled={!canEdit || isVatControlEnabled}
                    error={errors.postalCode}
                    value={customer.postalCode}
                    style={{ marginTop: 20 }}
                    onChange={e => this.onChangeValue('postalCode', false, e.target.value)}
                  />
                  <TextField
                    id="outlined-basic"
                    label={<ReduxLanguage languageKey={'forms.customerPrincipalSubDivision'} />}
                    variant="outlined"
                    fullWidth
                    disabled={!canEdit || isVatControlEnabled}
                    error={errors.principalSubDivision}
                    value={customer.principalSubDivision}
                    style={{ marginTop: 20 }}
                    onChange={e => this.onChangeValue('principalSubDivision', false, e.target.value)}
                  />
                  </>
                ): null}
            </div>
          </div>
          {!isVar ? (
            <div style={{ opacity: !isVatControlEnabled ? 1 : 0.6 }}>
              <Divider style={{ marginTop: 20 }} />
              <div>
                <h2 style={{ fontWeight: 'bold', marginTop: 20 }}>{<ReduxLanguage languageKey={'forms.customerAdditionalData'} />}</h2>
              </div>
              {!isSimplifiedCustomer ? (
                <TextField
                  id="outlined-basic"
                  label={<ReduxLanguage languageKey={'forms.customerCrmId'} />}
                  variant="outlined"
                  fullWidth
                  required
                  disabled={!canEdit || isVatControlEnabled}
                  error={errors.crmId}
                  value={customer.crmId}
                  style={{ marginTop: 20 }}
                  onChange={e => this.onChangeValue('crmId', true, e.target.value)}
                />
              ) : null}
              {!isSimplifiedCustomer ? (
                <Select
                  options={(marketplaces as PaginatedDataDTO<IMarketplaceInDTO>).content}
                  isClearable
                  getOptionLabel={o => o.name}
                  value={customer.marketplace}
                  isDisabled={!isSofiaAdmin || Boolean(customerToEdit)}
                  isOptionSelected={(o) => customer.marketplace?.id === o.id}
                  placeholder={<><ReduxLanguage languageKey={'forms.customerMarketplace'} /> *</>}
                  onChange={(mktplace) => this.onChangeValue('marketplace', true, mktplace)}
                  styles={{
                    menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
                    control: (styles) => ({ ...styles, minHeight: 55, marginTop: 20 }),
                  }} 
                />
              ): null}
              <Select
                options={(clusters as PaginatedDataDTO<IClusterInDTO>).content}
                isClearable
                getOptionLabel={o => `[${o.market}] - ${o.name}`}
                formatOptionLabel={(o) => <>[{o.market}] - <b>{o.name}</b></>}
                value={customer.cluster}
                isDisabled={customerToEdit || !canEdit || isVatControlEnabled ? true : false}
                isOptionSelected={(o) => customer.cluster?.id === o.id}
                placeholder={<><ReduxLanguage languageKey={'forms.customerCluster'} /> *</>}
                onChange={(data) => this.onChangeValue('cluster', true, data)}
                styles={{
                  menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
                  control: (styles) => ({ ...styles, minHeight: 55, marginTop: 20 }),
                }} 
              />
              <Select
                options={ISEOCompanies}
                isClearable
                getOptionLabel={o => `[${o.country}] - ${o.name}`}
                isDisabled={!canEdit || isVatControlEnabled ? true : false}
                value={_.find(ISEOCompanies, { name: customer.company })}
                formatOptionLabel={(o) => <>[{o.country}] - <b>{o.name}</b></>}
                isOptionSelected={(o, selectedValues) => _.findIndex(selectedValues, (sv) => sv === o) >= 0}
                placeholder={<><ReduxLanguage languageKey={'forms.customerCompany'} /> *</>}
                onChange={(data) => this.onChangeValue('company', true, data?.name)}
                styles={{
                  menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
                  control: (styles) => ({ ...styles, minHeight: 55, marginTop: 20 }),
                }} 
              />
              {isSimplifiedCustomer ? (
                <Autocomplete
                  options={(kams as PaginatedDataDTO<IKamInDTO>)?.content?.map(e => e.email) || []}
                  autoComplete
                  freeSolo
                  value={customer.kamEmail || ''}
                  disabled={!canEdit || isVatControlEnabled}
                  getOptionLabel={(option) => option}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      style={{ marginTop: 20 }}
                      error={errors.kamEmail}
                      label={<ReduxLanguage languageKey={'forms.customerKamEmail'} />}
                    />
                  )}
                  onInputChange={(e, data) => {
                    const next = _.trim(data || '');
                    const nextCustomer = { ...this.state.customer, kamEmail: next };
                    this.setState({ customer: nextCustomer });
                  }}
                />
              ) : null}
              <Select
                options={(vars as PaginatedDataDTO<IVarInDTO>).content}
                isClearable
                isDisabled={!canEdit || isVatControlEnabled ? true : false}
                getOptionLabel={o => o.name}
                formatOptionLabel={(o) => <>{o.name}</>}
                value={customer.VAR}
                isOptionSelected={(o) => customer.VAR?.id === o.id}
                placeholder={<><ReduxLanguage languageKey={'forms.customerVar'} /></>}
                onChange={(data) => this.onChangeValue('VAR', false, data)}
                styles={{
                  menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
                  control: (styles) => ({ ...styles, minHeight: 55, marginTop: 20 }),
                }}
              />
            </div>
          ) : null}
          {!canEdit?null:(
            <Button
              variant="contained"
              disabled={!this.canSubmitForm()}
              style={{ width: '100%', opacity: this.canSubmitForm() ? 1 : 0.5, backgroundColor: '#5AC0B1', color: 'white', marginTop: 50, fontWeight: 'bold'  }}
              onClick={() => this.onSubmitForm()}
            >
              <ReduxLanguage languageKey="forms.save" />
            </Button>
          )}
          
        </div>
      </MuiPickersUtilsProvider>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    marketplaces: state.credits.marketplaces.data,
    clusters: state.credits.clusters.data,
    vars: state.vars.vars.data,
    kams: state.activations.kams.data,
  };
}

const connector = connect(mapStateToProps);

export default connector(withStyles(styles)(CustomerForm));